import React from 'react';
import { BookOpen, User } from 'lucide-react';
import { UI_TEXT } from '../utils/constants';

// Outlined bookmark SVG
const BookmarkOutline = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
);

// Filled bookmark SVG
const BookmarkFilled = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M5 3a2 2 0 0 0-2 2v16l9-4 9 4V5a2 2 0 0 0-2-2H5z" />
  </svg>
);

const BookCard = ({ book, isSaved = false, onToggleSave }) => {
  return (
    <article
      className="
        group relative flex flex-col
        rounded-2xl overflow-hidden
        bg-white
        transition-all duration-300 ease-out motion-reduce:transition-none motion-reduce:hover:transform-none
        hover:-translate-y-1
      "
      style={{ boxShadow: '0 20px 40px rgba(83, 97, 94, 0.08)' }}
    >
      {/* Bookmark toggle button */}
      {onToggleSave && (
        <button
          onClick={() => onToggleSave(book)}
          className={`absolute top-4 right-4 z-10 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-amber-400/50 rounded ${
            isSaved
              ? 'text-amber-500'
              : 'text-gray-300 hover:text-amber-400'
          }`}
          aria-label={isSaved ? UI_TEXT.removeFromList : UI_TEXT.addToList}
          aria-pressed={isSaved}
        >
          {isSaved
            ? <BookmarkFilled className="h-5 w-5" />
            : <BookmarkOutline className="h-5 w-5" />
          }
        </button>
      )}

      {/* Gradient top accent bar — amber */}
      <div
        className="h-1 w-full flex-shrink-0"
        style={{ background: 'linear-gradient(90deg, #FF8C37 0%, #c85e00 60%, #984800 100%)' }}
        aria-hidden="true"
      />

      {/* Card body */}
      <div className="flex flex-col flex-1 p-6 pt-5">

        {/* Title */}
        <h3 className="text-lg font-bold text-on-surface mb-1.5 flex items-start gap-2 leading-snug font-manrope">
          <BookOpen
            className="h-5 w-5 text-primary flex-shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <span>{book.title}</span>
        </h3>

        {/* Author */}
        <p className="text-gray-600 text-sm mb-4 flex items-center gap-2">
          <User className="h-4 w-4 text-gray-500 flex-shrink-0" aria-hidden="true" />
          <span className="italic">{book.author}</span>
        </p>

        {/* Genre pill + age badge + новинка */}
        <div data-testid="badges" className="flex flex-wrap items-center gap-2 mb-4">
          {book.isNew && (
            <span data-testid="is-new" className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
              {UI_TEXT.isNew}
            </span>
          )}
          {book.genre && (
            <span data-testid="genre" className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold bg-secondary-fixed text-secondary">
              {book.genre}
            </span>
          )}
          {book.ageCategory && (
            <span data-testid="age-category" className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary-fixed text-on-primary-container">
              {book.ageCategory}
            </span>
          )}
        </div>

        {/* Secondary metadata */}
        <div className="space-y-1.5 text-sm text-gray-600 mb-3">
          {book.pubYear && (
            <div data-testid="pub-year" className="flex justify-between">
              <span>{UI_TEXT.pubYear}:</span>
              <span className="font-medium text-gray-700">{book.pubYear}</span>
            </div>
          )}
          {book.origYear && (
            <div data-testid="orig-year" className="flex justify-between">
              <span>{UI_TEXT.origYear}:</span>
              <span className="font-medium text-gray-700">{book.origYear}</span>
            </div>
          )}
          {book.isbn && (
            <div data-testid="isbn" className="flex justify-between">
              <span>{UI_TEXT.isbn}:</span>
              <span className="font-medium text-gray-600 text-xs tracking-wide">{book.isbn}</span>
            </div>
          )}
        </div>

        {/* Description */}
        {book.description && (
          <p data-testid="description" className="mt-auto pt-3 border-t border-outline-variant/25 text-gray-700 text-xs leading-relaxed">
            {book.description}
          </p>
        )}
      </div>
    </article>
  );
};

export default BookCard;
